/* eslint-disable @typescript-eslint/no-explicit-any */
import { GluonEnvironment, GluonRole, hasGluonRole, JwtPayload } from '@numeo/types-core'
import axios from 'axios'

export const AUTH_TOKEN = 'accessToken'

const decodeTokenEnvironment = (): GluonEnvironment | undefined => {
    const token = localStorage.getItem(AUTH_TOKEN)
    if (!token) return undefined
    const parts = token.split('.')
    if (parts.length < 2) return undefined
    try {
        const payload = JSON.parse(atob(parts[1]))
        const env = payload?.environment
        if (env === GluonEnvironment.PRODUCTION || env === GluonEnvironment.DEVELOPMENT) {
            return env
        }
    } catch {
        return undefined
    }
    return undefined
}

export const decodeToken = (): JwtPayload | null => {
    const token = localStorage.getItem(AUTH_TOKEN)
    if (!token) return null
    const parts = token.split('.')
    if (parts.length < 2) return null
    try {
        const payload = JSON.parse(atob(parts[1])) as JwtPayload
        return payload
    } catch {
        return null
    }
}

export const hasRole = (role: GluonRole): boolean => hasGluonRole(decodeToken()?.roles, role)

// Get environment-based URLs
const getEnvironmentUrls = (environment: GluonEnvironment) => {
    // Use environment-specific URLs from .env
    const baseUrl =
        (environment === GluonEnvironment.PRODUCTION ? process.env.REACT_APP_PROD_MAIN_SERVICE_URL : process.env.REACT_APP_DEV_MAIN_SERVICE_URL) ||
        'localhost:8080'

    // Check if URL already includes protocol
    const formatUrl = (url: string) => {
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url
        }
        // Add protocol if not present
        const protocol = window.location.protocol === 'https:' ? 'https' : 'http'
        return `${protocol}://${url}`
    }

    const formattedBaseUrl = formatUrl(baseUrl)
    let formattedLoadsIngestServiceUrl = 'http://localhost:8071'
    switch (environment) {
        case GluonEnvironment.DEVELOPMENT:
            formattedLoadsIngestServiceUrl = formatUrl(process.env.REACT_APP_DEV_LOADS_INGEST_SERVICE_URL || 'localhost:8071')
            break
        case GluonEnvironment.PRODUCTION:
            formattedLoadsIngestServiceUrl = formatUrl(process.env.REACT_APP_PROD_LOADS_INGEST_SERVICE_URL || 'localhost:8071')
            break
    }

    let formattedGeneralAgentServiceUrl = 'http://localhost:8090'
    switch (environment) {
        case GluonEnvironment.DEVELOPMENT:
            formattedGeneralAgentServiceUrl = formatUrl(process.env.REACT_APP_DEV_GENERAL_AGENT_SERVICE_HOST || 'localhost:8090')
            break
        case GluonEnvironment.PRODUCTION:
            formattedGeneralAgentServiceUrl = formatUrl(process.env.REACT_APP_PROD_GENERAL_AGENT_SERVICE_HOST || 'localhost:8090')
            break
    }

    let formattedOracleServiceUrl = 'http://localhost:8095'
    switch (environment) {
        case GluonEnvironment.DEVELOPMENT:
            formattedOracleServiceUrl = formatUrl(process.env.REACT_APP_DEV_CUSTOMER_KB_SERVICE_URL || 'dev-kb.numeo.ai')
            break
        case GluonEnvironment.PRODUCTION:
            formattedOracleServiceUrl = formatUrl(process.env.REACT_APP_PROD_CUSTOMER_KB_SERVICE_URL || 'kb.numeo.ai')
            break
    }

    return {
        API_URL: formattedBaseUrl,
        LOADS_INGEST_SERVICE_URL: formattedLoadsIngestServiceUrl,
        GENERAL_AGENT_SERVICE_URL: formattedGeneralAgentServiceUrl,
        CUSTOMER_KB_SERVICE_URL: formattedOracleServiceUrl,
    }
}

// Get environment derived from token; fallback to stored env or development
export const getStoredEnvironment = (): GluonEnvironment => {
    const tokenEnv = decodeTokenEnvironment()
    if (tokenEnv) {
        // Keep localStorage in sync with the token environment to prevent tampering
        localStorage.setItem('selected_environment', tokenEnv)
        return tokenEnv
    }
    const stored = localStorage.getItem('selected_environment')
    return stored === GluonEnvironment.PRODUCTION || stored === GluonEnvironment.DEVELOPMENT ? stored : GluonEnvironment.DEVELOPMENT
}

// Default URLs using stored environment
const defaultUrls = getEnvironmentUrls(getStoredEnvironment())

export const loadsIngestServiceAxios = axios.create({
    baseURL: defaultUrls.LOADS_INGEST_SERVICE_URL + '/v1',
    headers: {
        Accept: 'application/json',
    },
})

// loads-ingest routes that carry their own per-resource version (e.g.
// `/market-intel/lanes/price/v1`) mount at the service root, not under /v1.
// Same auth as loadsIngestServiceAxios. baseURL is set in setupAxios() so it
// follows environment switches the same way mainServiceRootAxios does.
export const loadsIngestServiceRootAxios = axios.create({
    headers: {
        Accept: 'application/json',
    },
})

const generalAgentServiceAxios = axios.create({
    baseURL: defaultUrls.GENERAL_AGENT_SERVICE_URL + '/v1',
    headers: {
        Accept: 'application/json',
    },
})

export const oracleServiceAxios = axios.create({
    baseURL: defaultUrls.CUSTOMER_KB_SERVICE_URL + '/v1',
    headers: {
        Accept: 'application/json',
    },
})

// New-convention main-service routes (e.g. mailbox.router.ts, proxy-allowlist)
// mount at the API root, not under /v1 like the rest of main-service — see
// ARCHITECTURE.md § Routing Conventions. baseURL is set inside setupAxios(),
// not here, so it stays in sync with environment switches the same way
// axios.defaults.baseURL does (the three instances above only resolve
// defaultUrls once at module load).
export const mainServiceRootAxios = axios.create({
    headers: {
        Accept: 'application/json',
    },
})

// Apply auth interceptors to direct-service axios instances
const setupServiceAxiosAuth = (instance: typeof axios) => {
    instance.interceptors.request.use(
        (config: any) => {
            const accessToken = localStorage.getItem(AUTH_TOKEN)
            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`
            }
            if (process.env.REACT_APP_RELEASE_DATE) {
                config.headers['Release-Date'] = process.env.REACT_APP_RELEASE_DATE
            }
            return config
        },
        (err: any) => Promise.reject(err)
    )
}

const setupLoadsIngestServiceAxios = () => setupServiceAxiosAuth(loadsIngestServiceAxios as any)
const setupLoadsIngestServiceRootAxios = () => setupServiceAxiosAuth(loadsIngestServiceRootAxios as any)
const setupGeneralAgentServiceAxios = () => setupServiceAxiosAuth(generalAgentServiceAxios as any)
const setupOracleServiceAxios = () => setupServiceAxiosAuth(oracleServiceAxios as any)
const setupMainServiceRootAxios = () => setupServiceAxiosAuth(mainServiceRootAxios as any)

/**
 * Service Integration Architecture
 *
 * PRINCIPLE: Minimize direct service calls. Use main-service as API gateway.
 *
 * DEFAULT: All API calls go through main-service (axios.defaults.baseURL)
 * - Routes: /v1/gluon/*, /v1/ext/*, /v1/email/*, /v1/integrations/*, etc.
 * - Main-service proxies to specialized services (agents-service, email-service, etc.)
 *
 * EXCEPTIONS (justified):
 * 1. loads-ingest-service (loadsIngestServiceAxios)
 *    - Performance-critical: 10k+ req/min for load deduplication
 *    - Use case: Similarity search, load matching
 *    - loadsIngestServiceRootAxios: its root-mounted routes — the public Market
 *      Intel API (`/market-intel/*`, ADR-033 §4.6), which the module owns
 *
 * 2. socket-service (WebSocket)
 *    - Protocol requirement: WebSocket for real-time communication
 *    - Use case: Live negotiation updates, real-time messages
 *
 * 3. main-service new-convention routes (mainServiceRootAxios)
 *    - Still main-service, but mounted at the API root instead of /v1
 *      (ARCHITECTURE.md § Routing Conventions — each new-convention route
 *      carries its own per-resource /v1 suffix instead of the legacy
 *      blanket /v1/<domain> prefix)
 *    - Use case: mailbox.router.ts, proxy-allowlist
 *
 * INTEGRATED INTO MAIN-SERVICE:
 * - Eval Framework: Integrated directly into main-service
 *   - Location: apps/services/main-service/src/gluon/eval-framework/
 *   - Endpoints: /v1/gluon/eval-framework/* (evals, personas, templates, executions, broker-styles)
 *   - Technology: LangGraph, LangChain, Firestore, Langfuse
 *   - Calls Python agents-service via gRPC for AI agent interactions
 */

export default function setupAxios(on401?: () => void, environment?: GluonEnvironment) {
    // Get environment-specific URLs - use stored environment if not provided
    const selectedEnvironment = environment || getStoredEnvironment()
    const urls = getEnvironmentUrls(selectedEnvironment)

    // Set default axios configuration
    axios.defaults.baseURL = `${urls.API_URL}/v1`
    axios.defaults.headers.Accept = 'application/json'

    // New-convention main-service routes mount at the root, not /v1 — resolved
    // fresh here (not at module load, unlike the three instances below) so it
    // stays in sync with environment switches the same way axios.defaults.baseURL does.
    mainServiceRootAxios.defaults.baseURL = urls.API_URL
    loadsIngestServiceRootAxios.defaults.baseURL = urls.LOADS_INGEST_SERVICE_URL

    setupLoadsIngestServiceAxios()
    setupLoadsIngestServiceRootAxios()
    setupGeneralAgentServiceAxios()
    setupOracleServiceAxios()
    setupMainServiceRootAxios()

    // Add auth token to requests
    axios.interceptors.request.use(
        (config: any) => {
            const accessToken = localStorage.getItem(AUTH_TOKEN)
            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`
            }
            return config
        },
        (error: any) => Promise.reject(error)
    )

    // Handle 401 errors
    axios.interceptors.response.use(
        (response: any) => response,
        (error: any) => {
            if (error.response?.status === 401 && on401) {
                on401()
            }
            return Promise.reject(error)
        }
    )

    // Handle 401 errors for main-service new-convention routes
    mainServiceRootAxios.interceptors.response.use(
        (response: any) => response,
        (error: any) => {
            if (error.response?.status === 401 && on401) {
                on401()
            }
            return Promise.reject(error)
        }
    )
}
